export const DELETE_WIDGET_MODAL = 'deleteWidgetModal';

export const WIDGET_KEY_FIELD_PATH = ['id'];
export const WIDGET_SORT_FIELD_PATH = ['definition', 'name'];
export const WIDGET_FILTERS_PATH = ['definition', 'filter', 'filters'];

export const START_ASSESSMENT = 'start-assessment';
export const ASSESSMENT_LIST_VIEW = 'assessment-list';
export const BAR_CHART = 'vertical-bar-chart';
export const PIE_CHART = 'pie-chart';
export const LINE_CHART = 'line-chart';

export const CHART_TYPES = [BAR_CHART, PIE_CHART, LINE_CHART];

export const TEAM_CONFIGURATION = 'team';
export const SOLUTION_CONFIGURATION = 'solution';

export const DATE_IS = 'date is';
export const DATE_IS_TODAY = 'date is today';
export const DATE_IS_BEFORE = 'date is before';
export const DATE_IS_BETWEEN = 'date is between';
export const DATE_IS_AFTER = 'date is after';

export const defaultGroupByLevel = {
  field: '',
  consolidateBy: '',
  userLabel: '',
};

export const defaultFunction = {
  type: 'count',
  field: '',
  userLabel: '',
};

const defaultGroupByConfig = {
  levels: [defaultGroupByLevel],
};

const defaultFunctionConfig = {
  functions: [defaultFunction],
};

export const defaultDateRange = {
  field: 'createDate',
  operator: DATE_IS,
  comparator: 'everything',
  type: 'primary',
};

const defaultFilterConfig = {
  satisfyAll: true,
  filters: [],
  dateRange: defaultDateRange,
};

const filterFunctions = {
  field: '',
  operator: '',
  comparator: '',
};

const defaultSortConfig = {
  axis: 'noaxis',
  order: 'asc',
};

export const defaultFilterFunction = {
  ...filterFunctions,
  type: 'default',
};

export const WIDGET_TYPES = [
  {
    type: START_ASSESSMENT,
    label: 'Start App',
    defaultFields: { description: '' },
  },
  {
    defaultFields: {
      columns: [
        {
          label: 'assessmentRef',
          userLabel: 'Reference',
        },
        {
          label: 'assessmentCreator',
          userLabel: 'Creator',
        },
        {
          label: 'createDate',
          userLabel: 'Created On',
        },
      ],
      filter: defaultFilterConfig,
    },
    type: ASSESSMENT_LIST_VIEW,
    label: 'List View',
    configuration: [
      {
        type: 'columns',
      },
      {
        type: 'filter',
      },
    ],
  },
  {
    defaultFields: {
      groupBy: defaultGroupByConfig,
      measure: defaultFunctionConfig,
      filter: defaultFilterConfig,
      sort: defaultSortConfig,
      barPosition: 'side-by-side',
      maxGroupLimit: 0,
    },
    type: BAR_CHART,
    label: 'Bar Chart',
    configuration: [
      {
        type: 'groupBy',
      },
      {
        type: 'function',
      },
      {
        type: 'filter',
      },
    ],
  },
  {
    defaultFields: {
      groupBy: defaultGroupByConfig,
      measure: defaultFunctionConfig,
      filter: defaultFilterConfig,
      sort: defaultSortConfig,
      maxGroupLimit: 0,
    },
    type: PIE_CHART,
    label: 'Pie Chart',
    configuration: [
      {
        type: 'groupBy',
      },
      {
        type: 'function',
      },
      {
        type: 'filter',
      },
    ],
  },
  {
    defaultFields: {
      groupBy: defaultGroupByConfig,
      measure: defaultFunctionConfig,
      filter: defaultFilterConfig,
      sort: defaultSortConfig,
      maxGroupLimit: 0,
    },
    type: LINE_CHART,
    label: 'Line Chart',
    configuration: [
      {
        type: 'groupBy',
      },
      {
        type: 'function',
      },
      {
        type: 'filter',
      },
    ],
  },
];

export const CREATE_OPTIONS = [
  {
    label: 'Create new widget',
    value: 'create',
  },
  {
    label: 'Copy from existing widget',
    value: 'copy',
  },
];

/* Widget Fields */
export const FIELD_TYPE_VARIABLE = 'assessment-variable';
export const FIELD_TYPE_ASSESSMENT_META = 'assessment-metadata';
export const FIELD_TYPE_WORKFLOW_PAGE_REF = 'assessment-workflow-page-ref';
export const WIDGET_FIELD_TYPES = [
  FIELD_TYPE_VARIABLE,
  FIELD_TYPE_ASSESSMENT_META,
];

export const FIELD_VALUE_TYPE_NUMBER = 'number';
export const FIELD_VALUE_TYPE_BOOLEAN = 'boolean';
export const FIELD_VALUE_TYPE_LIST = 'list';
export const FIELD_VALUE_TYPE_STRING = 'string';
export const FIELD_VALUE_TYPE_TIME = 'timestamptz';
export const FIELD_VALUE_TYPE_OPTIONS = 'options';
export const FIELD_VALUE_TYPE_USER = 'user';
export const FIELD_VALUE_TYPE_USERS = 'users';
export const FIELD_VALUE_TYPE_FILE = 'file';
export const FIELD_VALUE_TYPE_DOC = 'doc';

export const FIELD_VALUE_TYPES = [
  FIELD_VALUE_TYPE_NUMBER,
  FIELD_VALUE_TYPE_BOOLEAN,
  FIELD_VALUE_TYPE_STRING,
  FIELD_VALUE_TYPE_TIME,
  FIELD_VALUE_TYPE_USER,
  FIELD_VALUE_TYPE_USERS,
  FIELD_VALUE_TYPE_FILE,
  FIELD_VALUE_TYPE_LIST,
  FIELD_VALUE_TYPE_DOC,
];

export const assessmentMetaLabelMap = {
  assessmentID: 'ID',
  assessmentName: 'Name',
  assessmentRef: 'Reference',
  appName: 'App Name',
  appVersion: 'App Version',
  assessmentCreator: 'Creator',
  completed: 'Completed',
  createDate: 'Created On',
  lastEdit: 'Last Edited',
  lastEditedBy: 'Last Edited By',
  assessmentOwners: 'Owners',
  status: 'Status',
  workflowGroupName: 'Workflow Group',
  isSensitive: 'Sensitive',
  solutionVersion: 'Solution Version',
  initialCompletedOn: 'Initial Completed On',
  creationMethod: 'Creation Method',
  statusCategory: 'Status Category',
};

export const getMetaFieldLabel = (label: string) => {
  const userLabel =
    assessmentMetaLabelMap[label as keyof typeof assessmentMetaLabelMap];
  return userLabel || label;
};

export const FILTER_OPERATORS = {
  NUMBER: [
    { label: 'equals', value: 'equals' },
    { label: 'is not equal', value: 'is not equal' },
    { label: 'less than', value: 'less' },
    { label: 'greater than', value: 'greater' },
    { label: 'less than or equal', value: 'less or equal' },
    { label: 'greater than or equal', value: 'greater or equal' },
  ],
  TIME: [
    { label: 'Before Trigger Date', value: 'before today' },
    { label: 'After Trigger Date', value: 'after today' },
  ],
  FILE: [
    { label: 'contains', value: 'contains' },
    { label: 'does not contain', value: 'does not contain' },
  ],
  STRING: [
    { label: 'contains', value: 'contains' },
    { label: 'does not contain', value: 'does not contain' },
    { label: 'equals', value: 'equals' },
    { label: 'is not equal', value: 'is not equal' },
  ],
  OPTIONS: [
    { label: 'includes any of', value: 'includes any of' },
    { label: 'excludes any of', value: 'excludes' },
  ],
  OPTIONS_REF: [
    { label: 'includes any of', value: 'includes any of' },
    { label: 'excludes any of', value: 'excludes' },
  ],
  BOOLEAN: [
    { label: 'equals', value: 'equals' },
    { label: 'is not equal', value: 'is not equal' },
  ],
};

export const DATE_OPERATOR_OPTIONS = [
  { label: 'is', value: DATE_IS },
  { label: 'is today', value: DATE_IS_TODAY },
  { label: 'is before', value: DATE_IS_BEFORE },
  { label: 'is between', value: DATE_IS_BETWEEN },
  { label: 'is after', value: DATE_IS_AFTER },
  { label: 'is last', value: 'date is last' },
  { label: 'is next', value: 'date is next' },
];

export const DATE_COMPARATOR_OPTIONS = [
  { label: 'week', value: 'week' },
  { label: 'month', value: 'month' },
  { label: 'year', value: 'year' },
  { label: 'quarter', value: 'quarter' },
  { label: '7 days', value: '7' },
  { label: '14 days', value: '14' },
  { label: '30 days', value: '30' },
  { label: '60 days', value: '60' },
  { label: '90 days', value: '90' },
  { label: '365 days', value: '365' },
];

export const TIME_ELAPSED_FILTER_VALUE_OPTIONS = [
  { label: 'days', value: 'days' },
  { label: 'hours', value: 'hours' },
  { label: 'minutes', value: 'minutes' },
];

export const DURATION_FILTER_VALUE_OPTIONS = [
  { label: 'years', value: 'years' },
  { label: 'months', value: 'months' },
  { label: 'weeks', value: 'weeks' },
  { label: 'days', value: 'days' },
];

export const BOOLEAN_FILTER_DROPDOWN_OPTIONS = [
  { label: 'True', value: 'true' },
  {
    label: 'False',
    value: 'false',
  },
];

export const FIXED_DATE_OPTIONS = [
  DATE_IS,
  DATE_IS_BEFORE,
  DATE_IS_BETWEEN,
  DATE_IS_AFTER,
];

export const MESSAGE_EMPTY_DEFINITIONS_TEAM =
  'This team does not have any widgets.';
export const MESSAGE_EMPTY_DEFINITIONS_APP =
  'This app does not have any widgets yet. Create the first one using the button above.';
export const MESSAGE_DURATION_FILTER_TOOLTIP = `Results will be filtered to show based on the calculation
• 1 year = 365 days
• 1 month = 30 days
• 1 week = 7 days`;

export const CREATION_METHOD_OPTIONS = [
  'Scheduled',
  'Manual',
  'Bulk Import',
  'API',
  'AI Assistant',
];

export const filterOptionLabelOverrideMap = {
  // for creation method we want to display 'AI Agent' instead of 'AI Assistant'
  creationMethod: {
    'AI Assistant': 'AI Agent',
  },
};

export const filterOptionLabelReverseOverrideMap = {
  // for creation method we want to map back to 'AI Assistant'
  creationMethod: {
    'AI Agent': 'AI Assistant',
  },
};
